import React, { useCallback, useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { observer } from 'mobx-react-lite';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';

import {
    Form,
    FormComputedAsync,
    HiddenField,
    useModal,
} from '@ratehub/base-ui';

import fetchFeaturedRatesSortedByLowest from '../functions/fetchFeaturedRatesSortedByLowest';
import RateFetchingOptionsPropType from '../definitions/RateFetchingOptionsPropType';
import MortgageRateCardSimple from './MortgageRateCardSimple';
import RateFilterSelector from './RateFilterSelector';


function RateSelectorCard({
    initialSelectedRate,
    selectedRateName,
    onFilterChange,
    rateFetchingOptions,
    preferredProvider,
    className,
    ...rateFilterProps
}) {
    const modal = useModal();
    // We don't want to call onFilterChange with the filters until the user has selected a rate;
    // What if user changes the filters, but then closes the modal without selecting a rate?
    const [ updatedFilters, setUpdatedFilters ] = useState(rateFetchingOptions);

    // State is needed to prevent re-fetching rates when the modal is open and the user selects a rate.
    // The only time we want to re-fetch rates is when our rateFetchingOptions have changed.
    // We don't want our state changes to trigger a re-fetch either;
    // Refs are used to prevent re-creating the callback functions when state changes.

    // Using this instead of modal.isOpen because shared modal state causes re-fetching of rates.
    const modalIdRef = useRef(null);

    // Only should fetch a new rate if the rate is invalidated (by changing the filters)
    const shouldPreventRefetchRateRef = useRef(false);
    // If the user has selected a rate, we want to remember their provider preference from that point onward.
    const selectedProviderRef = useRef(null);
    // If rate params have changed, we need to invalidate the selected rate.
    useEffect(
        () => {
            shouldPreventRefetchRateRef.current = false;
            setUpdatedFilters(rateFetchingOptions);
        },
        [ JSON.stringify(rateFetchingOptions) ],
    );

    // Callbacks to FormComputedAsync must be memoized to prevent infinite re-renders
    // Using refs as dependencies because we don't want to trigger a re-fetch when they change.
    const fetchLowestRate = useCallback(
        async(params, form) => {
            const selectedRate = form.getValue(selectedRateName);

            // Requirement: If a custom rate is selected, do not fetch a new rate.
            if (selectedRate && isCustomRate(selectedRate)) {
                return selectedRate;
            }

            const rates = await fetchFeaturedRatesSortedByLowest(params, { callerName: 'RateSelectorCard.fetchLowestRate' });
            const [ lowestRate ] = rates;
            const providerPreference = selectedProviderRef.current ?? preferredProvider;
            const newSelectedRate = (providerPreference ? rates.find(rate => rate.provider.slug === providerPreference) : null) ?? lowestRate; // default to lowest rate if preferred provider is not available

            // Requirement: Reset selected rate to lowest rate in case it's no longer available.
            form.setValue(selectedRateName, newSelectedRate);

            return newSelectedRate;
        },
        [ selectedRateName, preferredProvider, selectedProviderRef ],
    );

    // Using refs as dependencies because we don't want to trigger a re-fetch when they change.
    // Especially when the modal closes- closing shouldn't trigger a re-fetch.
    const onlyWhenParamsValidAndModalClosed = useCallback(
        params => !shouldPreventRefetchRateRef.current && modalIdRef.current === null && areValidParams(params),
        [ modalIdRef, shouldPreventRefetchRateRef ],
    );

    const getRateParams = useCallback(
        () => rateFetchingOptions,
        // Although FormComputedAsync does a deep comparison of the values returned by this function,
        // We still need to do a deep comparison in case of unstable references
        [ JSON.stringify(updatedFilters) ],
    );

    // We need to register a computed to fetch lowest rate, but don't need to make it public.
    const lowestRateName = `${selectedRateName}-lowestRate`;

    return (
        <>
            <HiddenField
                name={selectedRateName}
                value={initialSelectedRate}
            />

            <FormComputedAsync
                name={lowestRateName}
                getFormValues={getRateParams}
                onFetchValue={fetchLowestRate}
                onlyWhen={onlyWhenParamsValidAndModalClosed}
                initialValue={initialSelectedRate}
            >
                {(_, form) => { 
                    const displayedRate = form.getValue(selectedRateName) ?? form.getValue(lowestRateName);
                    const hasError = !!form.getValue(`${lowestRateName}Error`);
                    const isLoading = form.getValue(`${lowestRateName}IsFetching`);

                    function handleSelectRate(selectedRate) {
                        modal.close(modalIdRef.current);

                        shouldPreventRefetchRateRef.current = true; // prevent fetching a new rate until filters change
                        selectedProviderRef.current = isCustomRate(selectedRate) ? null : selectedRate.provider.slug; // remember their provider preference
                        modalIdRef.current = null; // allow re-fetching rates when filters change

                        form.setValue(selectedRateName, selectedRate);
                        onFilterChange(updatedFilters);
                    }

                    return (
                        <>
                            <MortgageRateCardSimple
                                rate={displayedRate}
                                hasError={hasError}
                                isLoading={isLoading}
                                // Since the down payment field has snapping behaviour on blur
                                //  we need to disable this button until the blur takes affect,
                                //  otherwise the users can use this button to blur the field
                                //  and then we can potentially fetch with an invalid value
                                isButtonDisabled={!areValidParams(updatedFilters)}
                                onClick={() => {
                                    const modalId = modal.open({
                                        children: (
                                            <Form>
                                                <h3
                                                    className={classNames(
                                                        'rh-text-align-center',
                                                        'rh-title-m',
                                                        'rh-mt-0',
                                                        'rh-mb-0_5',
                                                        'weight-medium',
                                                    )}
                                                >
                                                    <FormattedMessage
                                                        id="mtg-common.RateSelectorCard.modal.title"
                                                        defaultMessage="Select a rate"
                                                    />
                                                </h3>

                                                <StyledRateFilterSelector
                                                    {...rateFilterProps}
                                                    className="rh-mx-auto"
                                                    initialSelectedRate={displayedRate}
                                                    initialFilters={updatedFilters}
                                                    onSelectRate={handleSelectRate}
                                                    onFilterChange={setUpdatedFilters}
                                                />
                                            </Form>
                                        ),
                                        screenReaderLabel: (
                                            <FormattedMessage
                                                id="mtg-common.RateSelectorCard.modal.screenReaderLabel"
                                                defaultMessage="A dialogue box to select a rate"
                                            />
                                        ),
                                        showBranding: false,
                                        verticalAlignment: 'flex-start',
                                        onClose() {
                                            modalIdRef.current = null;
                                        },
                                    });

                                    // prevent card from re-fetching while rate selector modal is open
                                    modalIdRef.current = modalId; 
                                }}
                                className={className}
                                data-test-name="rate-selector-card"
                            />
                        </>
                    );
                }}
            </FormComputedAsync>
        </>
    );
}

const StyledRateFilterSelector = styled(RateFilterSelector)`
    max-width: 700px;
`;

function isCustomRate(rate) {
    return !rate.id;
}

// This assumes that all rate fetching params keys are passed, and some may be null (invalid)
function areValidParams(params) {
    return Object.values(params).every(param => param != null);
}

RateSelectorCard.propTypes = {
    selectedRateName: PropTypes.string.isRequired,
    rateFetchingOptions: RateFetchingOptionsPropType.isRequired,

    initialSelectedRate: PropTypes.shape({
        value: PropTypes.number.isRequired,
    }),
    onFilterChange: PropTypes.func,
    preferredProvider: PropTypes.string, // slug

    className: PropTypes.string,
};

RateSelectorCard.defaultProps = {
    initialSelectedRate: undefined,
    onFilterChange: undefined,
    className: undefined,
    preferredProvider: undefined,
};

export default observer(RateSelectorCard);
